import { Link, useLocation } from "react-router-dom";
import { Heart, Menu, X, UserPlus } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import DiwaliBanner from "@/components/campaign/DiwaliBanner";

const LOGO_URL = "https://customer-assets-m6fa6gv7.emergentagent.net/job_amtm-animal-care/artifacts/6ng7d8bg_amtm_logo_trim.png";
const GUARDIAN_PULSE_SEEN_KEY = "amtm_guardian_cta_pulse_seen";

export default function PublicHeader() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [showGuardianPulse, setShowGuardianPulse] = useState(
    () => typeof window !== "undefined" && !sessionStorage.getItem(GUARDIAN_PULSE_SEEN_KEY)
  );

  useEffect(() => {
    if (!showGuardianPulse) return;
    const timer = setTimeout(() => {
      setShowGuardianPulse(false);
      sessionStorage.setItem(GUARDIAN_PULSE_SEEN_KEY, "1");
    }, 4000);
    return () => clearTimeout(timer);
  }, [showGuardianPulse]);

  const links = [
    { to: "/", label: "Home" },
    { to: "/cases", label: "Animals in Our Care" },
    { to: "/sanctuary", label: "Sanctuary" },
    { to: "/success-stories", label: "Success Stories" },
  ];

  return (
    <>
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-amtm-bg/80 border-b border-amtm-border" data-testid="public-header">
      <div className="max-w-6xl mx-auto px-6 md:px-8 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="public-header-logo-link">
          <img src={LOGO_URL} alt="AMTM - Animal Matter To Me" className="h-14 w-auto object-contain" />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`public-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-sm font-semibold transition-colors ${
                location.pathname === l.to ? "text-amtm-giveText" : "text-amtm-text hover:text-amtm-giveText"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <Button asChild size="sm" className="relative rounded-full bg-amtm-teal text-white hover:bg-amtm-teal/90 shadow-md" data-testid="public-header-guardian-cta">
            <Link to="/guardians">
              {showGuardianPulse && (
                <span className="absolute -top-1 -right-1 flex h-3 w-3" data-testid="guardian-cta-pulse-indicator">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amtm-give opacity-75" />
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-amtm-give" />
                </span>
              )}
              <UserPlus className="h-4 w-4 mr-1" /> Become a Guardian
            </Link>
          </Button>
          <Button asChild size="sm" className="rounded-full bg-amtm-give text-amtm-giveText hover:bg-amtm-giveHover shadow-md" data-testid="public-header-donate-cta">
            <Link to="/donate">
              <Heart className="h-4 w-4 mr-1" /> Donate
            </Link>
          </Button>
        </div>

        <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="public-header-mobile-toggle">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-amtm-border bg-amtm-bg px-6 py-4 flex flex-col gap-4" data-testid="public-header-mobile-menu">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-sm font-semibold text-amtm-text">
              {l.label}
            </Link>
          ))}
          <div className="flex flex-col gap-2.5 pt-2">
            <Button
              asChild
              className="relative w-full rounded-full bg-amtm-teal text-white hover:bg-amtm-teal/90 shadow-md"
              data-testid="public-header-mobile-guardian-cta"
              onClick={() => setOpen(false)}
            >
              <Link to="/guardians">
                {showGuardianPulse && (
                  <span className="absolute -top-1 right-3 flex h-3 w-3" data-testid="guardian-cta-mobile-pulse-indicator">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amtm-give opacity-75" />
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-amtm-give" />
                  </span>
                )}
                <UserPlus className="h-4 w-4 mr-1.5" /> Become a Guardian
              </Link>
            </Button>
            <Button
              asChild
              className="w-full rounded-full bg-amtm-give text-amtm-giveText hover:bg-amtm-giveHover shadow-md"
              data-testid="public-header-mobile-donate-cta"
              onClick={() => setOpen(false)}
            >
              <Link to="/donate">
                <Heart className="h-4 w-4 mr-1.5" /> Donate
              </Link>
            </Button>
          </div>
        </div>
      )}
      </header>
      <DiwaliBanner />
    </>
  );
}
